import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { festivals as festivalsApi, setFestivalIdGetter } from '../services/api';
import { useAuth } from './AuthContext';

const FestivalContext = createContext(null);

// Provider loads the list of festivals and the active one, lets users switch
// which festival the UI is viewing. The selected festival id is injected into
// every API call via the axios interceptor registered in services/api.js.
//
// The festival list endpoint requires auth, and the festival selector UI is
// only rendered for authenticated users (see layout/Navigation.js and the
// admin shell), so we defer the fetch until the user is logged in. Anonymous
// pages like Home and MapView resolve the active festival server-side on
// their own and don't need this provider's data.
export const FestivalProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Keep a ref so the axios interceptor (closure) always sees the latest value
  // even when the React tree hasn't re-rendered.
  const selectedIdRef = useRef(null);
  selectedIdRef.current = selectedId;

  useEffect(() => {
    setFestivalIdGetter(() => selectedIdRef.current);
  }, []);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const all = await festivalsApi.list();
      setList(all);
      const activeOne = all.find(f => f.is_active) || null;
      setActive(activeOne);
      setSelectedId(prev => prev ?? activeOne?.id ?? null);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      // Reset any state from a previous session so a logged-out user doesn't
      // see stale data and we don't keep a dangling festival id getter.
      setList([]);
      setActive(null);
      setSelectedId(null);
      setError(null);
      setLoading(false);
      return;
    }
    refresh();
  }, [authLoading, isAuthenticated, refresh]);

  const selected = useMemo(
    () => list.find(f => f.id === selectedId) || active,
    [list, selectedId, active]
  );

  // True when the user is browsing a festival that is no longer accepting
  // changes (archived/closed, or not the active one). Used by UI to gate edits.
  const isReadOnly = useMemo(() => {
    if (!selected) return true;
    if (!active) return true;
    return selected.id !== active.id || selected.status === 'archived' || selected.status === 'closed';
  }, [selected, active]);

  const value = {
    list,
    active,
    selected,
    selectedId: selected?.id ?? null,
    setSelectedId,
    isReadOnly,
    loading,
    error,
    refresh,
  };

  return (
    <FestivalContext.Provider value={value}>
      {children}
    </FestivalContext.Provider>
  );
};

export const useFestival = () => {
  const ctx = useContext(FestivalContext);
  if (!ctx) {
    throw new Error('useFestival must be used within a FestivalProvider');
  }
  return ctx;
};
